import React, { useEffect, useState, useCallback } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import BASE_URL from '../../api';
import {
  Card,
  Row,
  Col,
  Statistic,
  Spin,
  message,
  Modal,
  Table,
  Tag,
  Typography,
  Button,
  Steps,
  Divider,
  Input,
  Space,
} from 'antd';
import {
  FaTruck,
  FaBoxOpen,
  FaFileInvoice,
  FaFileAlt,
  FaEye,
  FaDownload,
} from 'react-icons/fa';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import SiteLogo from '../../assets/images/SparkLogistics.jpg';

const { Title, Text } = Typography;
const { Step } = Steps;
const { Search } = Input;

const CustomerDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [customerData, setCustomerData] = useState(null);
  const [activeModal, setActiveModal] = useState(null);
  const [modalData, setModalData] = useState([]);
  const [trackingDetails, setTrackingDetails] = useState(null);
  const [trackingModalVisible, setTrackingModalVisible] = useState(false);
  const [searchText, setSearchText] = useState({
    pickRequests: '',
    onlineBookings: '',
    invoices: '',
  });
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceDetailVisible, setInvoiceDetailVisible] = useState(false);

  const fetchCustomerData = useCallback(async (id) => {
    if (!id) {
      message.warning('No customer selected');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const { data } = await axios.get(
        `${BASE_URL}/api/getctrl/customer/${id}/all-records?limit=1000`
      );
      setCustomerData(data);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load customer data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const customerId = localStorage.getItem('customerId') || localStorage.getItem('userId');
    if (customerId) {
      fetchCustomerData(customerId);
    } else {
      setLoading(false);
    }
  }, [fetchCustomerData]);

  const fetchTrackingDetails = async (docketNumber) => {
    const token = localStorage.getItem('token');
    const params = new URLSearchParams();
    params.append('docketnumber', String(docketNumber).trim());

    try {
      const response = await axios.post(`${BASE_URL}/api/onlinebookings/track`, params, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });
      if (response.data && response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error(response.data?.message || 'Tracking data not found for this docket');
    } catch (err) {
      console.error('Tracking fetch error:', err);
      throw new Error(err.response?.data?.message || err.message || 'Tracking data not found for this docket');
    }
  };

  const handleTrackClick = async (record) => {
    const docketNumber = record.docketNumber || record.docket;
    if (!docketNumber) {
      message.warning('No docket number available for this booking');
      return;
    }

    setTrackingModalVisible(true);
    setTrackingDetails(null);

    try {
      const raw = await fetchTrackingDetails(docketNumber);

      const consignee = raw.consigneeid || {};
      const origin = raw.originlocation || {};
      const dest = raw.destlocation || {};
      const rawStatus = raw.booingid?.status || raw.docketstatus || 'Unknown';
      const statusLower = rawStatus.toLowerCase();
      const isDelivered = statusLower.includes('delivered') || statusLower.includes('completed') || statusLower.includes('pod') || raw.docketstatus === 'Completed';
      const isArrived = isDelivered || statusLower.includes('arrived') || statusLower.includes('reached') || statusLower.includes('manifest in');
      const isInTransit = isArrived || statusLower.includes('transit') || statusLower.includes('intransit') || statusLower.includes('manifest out') || statusLower.includes('dispatched');

      let podUrl = null;
      let podFileName = null;
      if (raw.file) {
        podFileName = raw.file.split(/[\\/]/).pop();
        podUrl = `${BASE_URL}/${raw.file.replace(/\\/g, '/')}`;
      }

      const displayConsignee = raw.consigneeTOaddress || consignee.companyName || consignee.name || 'Consignee';
      const displayStatus = isDelivered ? 'Delivered' : (raw.booingid?.status || raw.docketstatus || 'Initiated');

      const steps = [
        {
          title: 'Booked',
          description: (raw.bookingdate || raw.booingid?.bookingdate)
            ? new Date(raw.bookingdate || raw.booingid?.bookingdate).toLocaleDateString('en-IN', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            })
            : 'Booking confirmed',
          status: 'finish',
        },
        {
          title: 'In Transit',
          description: isInTransit
            ? `Dispatched from ${origin.areaname || origin.hub || 'Origin'}`
            : 'Pending dispatch',
          status: isInTransit ? 'finish' : 'wait',
        },
        {
          title: 'Arrived',
          description: isArrived
            ? `Reached ${dest.areaname || dest.hub || 'Destination'}`
            : 'Pending arrival',
          status: isArrived ? 'finish' : 'wait',
        },
        {
          title: 'Delivered',
          description: isDelivered
            ? `Delivered to ${displayConsignee}`
            : 'Pending delivery',
          status: isDelivered ? 'finish' : 'wait',
        },
      ];

      let currentStep = 0;
      if (isDelivered) currentStep = 3;
      else if (isArrived) currentStep = 2;
      else if (isInTransit) currentStep = 1;

      setTrackingDetails({
        docket: raw.docket || docketNumber,
        status: displayStatus,
        booked: (raw.bookingdate || raw.booingid?.bookingdate)
          ? new Date(raw.bookingdate || raw.booingid?.bookingdate).toLocaleDateString('en-IN')
          : '—',
        consigneeName: displayConsignee,
        consigneeAddress: consignee.deliveryAddress || consignee.address || '—',
        origin: origin.areaname || origin.hub || '—',
        destination: dest.areaname || dest.hub || '—',
        amount: raw.grandtotal > 0 ? `₹${Number(raw.grandtotal).toLocaleString('en-IN')}` : 'Paid',
        steps,
        currentStep,
        hasPOD: !!raw.file,
        podUrl,
        podFileName,
      });
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Failed to load tracking information');
      setTrackingModalVisible(false);
    }
  };

  const handleViewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setInvoiceDetailVisible(true);
  };

  const openModal = (type, data) => {
    setActiveModal(type);
    setModalData(data || []);
  };

  const closeModal = () => {
    setActiveModal(null);
    setModalData([]);
  };

  const getFilteredData = (dataList, query, fieldsToSearch) => {
    if (!query) return dataList;
    const lowerQuery = query.toLowerCase();
    return dataList.filter((item) =>
      fieldsToSearch.some((field) => {
        const val = item[field];
        return val && String(val).toLowerCase().includes(lowerQuery);
      })
    );
  };

  const pickRequestColumns = [
    { title: 'Pick No', dataIndex: 'pickrequestnumber', key: 'pickrequestnumber', fixed: 'left' },
    {
      title: 'Date',
      dataIndex: 'bookingdate',
      key: 'bookingdate',
      render: (date) => (date ? new Date(date).toLocaleDateString('en-IN') : '-'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        let color = 'default';
        if (status?.toLowerCase().includes('completed')) color = 'green';
        else if (status?.toLowerCase().includes('picked')) color = 'blue';
        else if (status?.toLowerCase().includes('initiated')) color = 'orange';
        return <Tag color={color}>{status || 'Unknown'}</Tag>;
      },
    },
    { title: 'Boxes', dataIndex: 'noofbox', key: 'noofbox', align: 'center' },
    { title: 'Weight (kg)', dataIndex: 'actweight', key: 'actweight' },
    { title: 'Pincode', dataIndex: 'pickuppin', key: 'pickuppin' },
    { title: 'Location', dataIndex: 'location', key: 'location' },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => new Date(date).toLocaleString('en-IN'),
    },
  ];

  const onlineBookingColumns = [
    {
      title: 'Docket No',
      dataIndex: 'docketNumber',
      key: 'docketNumber',
      render: (val, record) => val || record.docket || '-',
    },
    {
      title: 'Consignee',
      key: 'consignee',
      render: (_, record) =>
        record.consigneeTOaddress ||
        (typeof record.consigneeid === 'object' ? (record.consigneeid?.companyName || record.consigneeid?.name) : null) ||
        record.consignee?.companyName ||
        record.consignee?.name ||
        '—',
    },
    {
      title: 'Origin',
      key: 'origin',
      render: (_, record) =>
        (typeof record.originlocation === 'object' ? (record.originlocation?.areaname || record.originlocation?.hub) : null) ||
        record.origin?.areaname ||
        record.origin?.hub ||
        'CHENNAI',
    },
    {
      title: 'Destination',
      key: 'destination',
      render: (_, record) => {
        const orig = (typeof record.originlocation === 'object' ? (record.originlocation?.areaname || record.originlocation?.hub) : null) || record.origin?.areaname || 'CHENNAI';
        let dest =
          record.outgoingManifest?.desthub?.branchname ||
          record.outgoingManifest?.desthub?.branchcode ||
          (typeof record.location === 'object' ? (record.location?.areaname || record.location?.hub) : null) ||
          (typeof record.destlocation === 'object' ? (record.destlocation?.areaname || record.destlocation?.hub) : null) ||
          record.dest?.areaname ||
          record.dest?.hub ||
          record.topin?.locationname ||
          record.topin?.district ||
          '—';
        if (dest && orig && dest.trim().toUpperCase() === orig.trim().toUpperCase()) {
          const alt = (typeof record.location === 'object' ? (record.location?.areaname || record.location?.hub) : null) || record.topin?.locationname;
          if (alt && alt.trim().toUpperCase() !== orig.trim().toUpperCase()) {
            dest = alt;
          }
        }
        return dest;
      },
    },
    { title: 'Boxes', dataIndex: 'noofbox', key: 'noofbox', align: 'center', render: (v, r) => v || r.noofpiece || 1 },
    { title: 'Act. Wt (kg)', dataIndex: 'actweight', key: 'actweight' },
    { title: 'Vol. Wt (kg)', dataIndex: 'volweight', key: 'volweight', render: (v, r) => v || r.totvolweight || 0 },
    { title: 'Mode', dataIndex: 'mode', key: 'mode', render: (v) => v || 'Surface' },
    {
      title: 'Booked On',
      dataIndex: 'bookingdate',
      key: 'bookingdate',
      render: (date) => (date ? new Date(date).toLocaleDateString('en-IN') : '-'),
    },
    {
      title: 'Action',
      key: 'action',
      width: 100,
      render: (_, record) => (
        <Button size="small" type="primary" onClick={() => handleTrackClick(record)}>
          Track
        </Button>
      ),
    },
  ];

  const invoiceColumns = [
    { title: 'Invoice No', dataIndex: 'invoiceNumber', key: 'invoiceNumber', fixed: 'left' },
    {
      title: 'Amount',
      dataIndex: 'grandTotal',
      key: 'grandTotal',
      render: (amt) => (amt ? `₹${Number(amt).toLocaleString('en-IN')}` : '₹0'),
    },
    {
      title: 'Status',
      render: (_, record) => {
        const paid = record.totalCollected || 0;
        const total = record.grandTotal || 0;
        let status = 'Pending';
        let color = 'orange';
        if (paid >= total) {
          status = 'Paid';
          color = 'green';
        } else if (paid > 0) {
          status = 'Partially Paid';
          color = 'blue';
        }
        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: 'Period',
      render: (_, r) =>
        r.billMonth ||
        `${new Date(r.fromDate || r.invoiceDate).toLocaleDateString('en-IN')} – ${new Date(
          r.toDate || r.invoiceDate
        ).toLocaleDateString('en-IN')}`,
    },
    {
      title: 'Issued',
      dataIndex: 'invoiceDate',
      render: (d) => (d ? new Date(d).toLocaleDateString('en-IN') : '-'),
    },
    {
      title: 'View',
      key: 'view',
      width: 80,
      render: (_, record) => (
        <Button
          type="text"
          icon={<FaEye />}
          onClick={() => handleViewInvoice(record)}
        />
      ),
    },
  ];

  if (loading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f0f2f5',
        }}
      >
        <ToastContainer />
        <Spin size="large" />
      </div>
    );
  }

  if (!customerData?.customer) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f0f2f5',
        }}
      >
        <Title level={4} type="secondary">
          No customer data found
        </Title>
        <Text type="secondary">Please log in with a valid customer account.</Text>
      </div>
    );
  }

  const { customer, pickRequests = {}, onlineBookings = {}, invoices = {} } = customerData;

  const uniqueBookings = onlineBookings.data
    ? Array.from(new Map(onlineBookings.data.map((item) => [item._id, item])).values())
    : [];

  const filteredPickRequests = getFilteredData(pickRequests.data || [], searchText.pickRequests, [
    'pickrequestnumber',
    'status',
    'location',
    'pickuppin',
  ]);

  const filteredOnlineBookings = getFilteredData(uniqueBookings, searchText.onlineBookings, [
    'docketNumber',
    'mode',
  ]);

  const filteredInvoices = getFilteredData(invoices.data || [], searchText.invoices, [
    'invoiceNumber',
    'billMonth',
  ]);

  const handleExportMISExcel = () => {
    try {
      if (!uniqueBookings || uniqueBookings.length === 0) {
        toast.info('No MIS report records available to export');
        return;
      }

      const dataToExport = uniqueBookings.map((item, index) => {
        const rawStatus = item.finalStatus || item.status || item.docketstatus || 'Initiated';
        const statusLower = String(rawStatus).toLowerCase();
        const isDelivered = statusLower.includes('delivered') || statusLower.includes('completed') || !!item.file;

        const consigneeVal =
          item.consigneeTOaddress ||
          (typeof item.consigneeid === 'object' ? (item.consigneeid?.companyName || item.consigneeid?.name) : null) ||
          item.consignee?.companyName ||
          item.consignee?.name ||
          (typeof item.consigneeid === 'string' ? item.consigneeid : null) ||
          '—';

        const originVal =
          (typeof item.originlocation === 'object' ? (item.originlocation?.areaname || item.originlocation?.hub || item.originlocation?.name) : null) ||
          item.origin?.areaname ||
          item.origin?.hub ||
          item.origin?.name ||
          (typeof item.originlocation === 'string' ? item.originlocation : null) ||
          'CHENNAI';

        let destVal =
          item.outgoingManifest?.desthub?.branchname ||
          item.outgoingManifest?.desthub?.branchcode ||
          (typeof item.location === 'object' ? (item.location?.areaname || item.location?.hub || item.location?.name) : null) ||
          (typeof item.destlocation === 'object' ? (item.destlocation?.areaname || item.destlocation?.hub || item.destlocation?.name) : null) ||
          item.dest?.areaname ||
          item.dest?.hub ||
          item.dest?.name ||
          item.topin?.locationname ||
          (typeof item.destlocation === 'string' && isNaN(item.destlocation) ? item.destlocation : null) ||
          item.topin?.district ||
          '—';

        if (destVal && originVal && destVal.trim().toUpperCase() === originVal.trim().toUpperCase()) {
          const altDest =
            (typeof item.location === 'object' ? (item.location?.areaname || item.location?.hub || item.location?.name) : null) ||
            item.topin?.locationname ||
            (item.topin?.pincode ? `PIN: ${item.topin.pincode}` : null);
          if (altDest && altDest.trim().toUpperCase() !== originVal.trim().toUpperCase()) {
            destVal = altDest;
          }
        }

        const consignorVal =
          item.consignorTOaddress ||
          (typeof item.consignorid === 'object' ? (item.consignorid?.companyName || item.consignorid?.name) : null) ||
          item.shipper?.companyName ||
          item.shipper?.name ||
          item.consignor?.companyName ||
          item.consignor?.name ||
          customer?.companyName ||
          customer?.name ||
          '—';

        return {
          'S.No': index + 1,
          'Docket Number': item.docketNumber || item.docket || '—',
          'Booking Date': item.bookingdate ? new Date(item.bookingdate).toLocaleDateString('en-IN') : '—',
          'Consignor': consignorVal,
          'Consignee': consigneeVal,
          'Origin Branch/Hub': originVal,
          'Destination Hub': destVal,
          'Mode': item.mode || 'Surface',
          'No of Boxes': item.noofbox || item.noofpiece || 1,
          'Actual Weight (kg)': item.actweight || 0,
          'Volumetric Weight (kg)': item.volweight || item.totvolweight || 0,
          'Delivery Status': isDelivered ? 'DELIVERED' : String(rawStatus).toUpperCase(),
          'POD Status': item.file ? 'Uploaded' : 'Pending',
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'MIS Report');

      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const dataBlob = new Blob([excelBuffer], { type: 'application/octet-stream' });
      saveAs(dataBlob, `Customer_MIS_Report_${(customer?.name || 'Customer').replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success('MIS Report exported to Excel successfully!');
    } catch (err) {
      console.error('Excel Export Error:', err);
      toast.error('Failed to export Excel report');
    }
  };

  const downloadInvoicePDF = (inv) => {
    try {
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 15;

      doc.addImage(SiteLogo, 'JPEG', 20, 5, 30, 25);
      doc.setFontSize(20).setFont('helvetica', 'bold');
      doc.setTextColor(40, 40, 40);
      doc.text('INVOICE', pageWidth - margin, 20, { align: 'right' });

      doc.setFontSize(10).setFont('helvetica', 'normal');
      doc.text('SPARK LOGISTICS SERVICES', pageWidth - margin, 26, { align: 'right' });
      doc.text('GSTIN: 33ABCDE1234F1Z5', pageWidth - margin, 31, { align: 'right' });
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, 35, pageWidth - margin, 35);

      doc.save(`Invoice_${inv?.invoiceNumber || 'Detail'}.pdf`);
    } catch (e) {
      console.error(e);
      toast.error('Failed to generate PDF');
    }
  };

  return (
    <div style={{ padding: 24, background: '#f0f2f5', minHeight: '100vh' }}>
      <ToastContainer />
      <Title level={2} style={{ marginBottom: 32 }}>
        Welcome back, {customer?.name || 'Customer'}!
      </Title>

      <Row gutter={[16, 24]}>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable style={{ background: 'linear-gradient(135deg, #e6f7ff, #bae7ff)' }}>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <FaTruck style={{ fontSize: 40, color: '#1890ff' }} />
            </div>
            <Statistic
              title="Pick Requests"
              value={pickRequests.total || pickRequests.data?.length || 0}
              valueStyle={{ color: '#1890ff' }}
            />
            <Button
              type="primary"
              style={{ marginTop: 16 }}
              onClick={() => openModal('pickRequests', pickRequests.data || [])}
            >
              View All
            </Button>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card hoverable style={{ background: 'linear-gradient(135deg, #f6ffed, #d9f7be)' }}>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <FaBoxOpen style={{ fontSize: 40, color: '#52c41a' }} />
            </div>
            <Statistic
              title="Online Bookings"
              value={onlineBookings.total || uniqueBookings.length || 0}
              valueStyle={{ color: '#52c41a' }}
            />
            <Button
              type="primary"
              style={{ marginTop: 16 }}
              onClick={() => openModal('onlineBookings', uniqueBookings)}
            >
              View All
            </Button>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card hoverable style={{ background: 'linear-gradient(135deg, #f0efff, #d3adf7)' }}>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <FaFileAlt style={{ fontSize: 40, color: '#722ed1' }} />
            </div>
            <Statistic
              title="MIS Reports"
              value={uniqueBookings.length || 0}
              valueStyle={{ color: '#722ed1' }}
            />
            <Button
              type="primary"
              style={{ marginTop: 16, background: '#722ed1', borderColor: '#722ed1' }}
              icon={<FaDownload />}
              onClick={handleExportMISExcel}
            >
              Export Excel
            </Button>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card hoverable style={{ background: 'linear-gradient(135deg, #fff7e6, #ffe7ba)' }}>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <FaFileInvoice style={{ fontSize: 40, color: '#fa8c16' }} />
            </div>
            <Statistic
              title="Invoices"
              value={invoices.total || invoices.data?.length || 0}
              valueStyle={{ color: '#fa8c16' }}
            />
            <Button
              type="primary"
              style={{ marginTop: 16 }}
              onClick={() => openModal('invoices', invoices.data || [])}
            >
              View All
            </Button>
          </Card>
        </Col>
      </Row>

      {/* ── Customer MIS Reports Section ──────────────────────────── */}
      <Card
        style={{ marginTop: 32, borderRadius: 12, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <Space>
              <FaFileAlt style={{ color: '#722ed1', fontSize: 22 }} />
              <Title level={4} style={{ margin: 0 }}>
                Customer MIS Reports & Shipments
              </Title>
            </Space>
            <Button
              type="primary"
              style={{ background: '#27ae60', borderColor: '#27ae60' }}
              icon={<FaDownload />}
              onClick={handleExportMISExcel}
            >
              Export MIS Excel
            </Button>
          </div>
        }
      >
        <Search
          placeholder="Search docket number, mode, consignee..."
          allowClear
          enterButton
          style={{ marginBottom: 16, maxWidth: 400 }}
          value={searchText.onlineBookings || ''}
          onChange={(e) => setSearchText((prev) => ({ ...prev, onlineBookings: e.target.value }))}
        />
        <Table
          columns={onlineBookingColumns}
          dataSource={filteredOnlineBookings}
          rowKey="_id"
          pagination={{ pageSize: 10, showSizeChanger: true }}
          scroll={{ x: 'max-content' }}
        />
      </Card>

      {/* ── Main Records Modal ────────────────────────────────────── */}
      <Modal
        title={
          <Space>
            {activeModal === 'pickRequests' && <FaTruck style={{ color: '#1890ff' }} />}
            {activeModal === 'onlineBookings' && <FaBoxOpen style={{ color: '#52c41a' }} />}
            {activeModal === 'invoices' && <FaFileInvoice style={{ color: '#fa8c16' }} />}
            <span>
              {activeModal === 'pickRequests'
                ? 'Pick Requests'
                : activeModal === 'onlineBookings'
                  ? 'Online Bookings'
                  : activeModal === 'invoices'
                    ? 'Invoices'
                    : 'Records'}
            </span>
          </Space>
        }
        open={!!activeModal}
        onCancel={closeModal}
        footer={null}
        width={activeModal === 'onlineBookings' || activeModal === 'pickRequests' ? 1100 : 900}
        centered
      >
        <Search
          placeholder="Search by number, status, location..."
          allowClear
          enterButton
          style={{ marginBottom: 16 }}
          value={searchText[activeModal] || ''}
          onChange={(e) =>
            setSearchText((prev) => ({ ...prev, [activeModal]: e.target.value }))
          }
        />

        {modalData.length > 0 ? (
          <>
            {activeModal === 'pickRequests' && (
              <Table
                columns={pickRequestColumns}
                dataSource={filteredPickRequests}
                rowKey="_id"
                pagination={{ pageSize: 10, showSizeChanger: true }}
                scroll={{ x: 'max-content' }}
              />
            )}
            {activeModal === 'onlineBookings' && (
              <Table
                columns={onlineBookingColumns}
                dataSource={filteredOnlineBookings}
                rowKey="_id"
                pagination={{ pageSize: 10, showSizeChanger: true }}
                scroll={{ x: 'max-content' }}
              />
            )}
            {activeModal === 'invoices' && (
              <Table
                columns={invoiceColumns}
                dataSource={filteredInvoices}
                rowKey="_id"
                pagination={{ pageSize: 10, showSizeChanger: true }}
              />
            )}
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <Title level={4} type="secondary">
              No records found
            </Title>
          </div>
        )}
      </Modal>

      {/* ── Tracking Modal with POD image ─────────────────────────── */}
      <Modal
        title={trackingDetails ? `Tracking – ${trackingDetails.docket}` : 'Tracking Details'}
        open={trackingModalVisible}
        onCancel={() => setTrackingModalVisible(false)}
        footer={null}
        width={760}
        centered
        zIndex={1200}
      >
        {!trackingDetails ? (
          <div style={{ textAlign: 'center', padding: '80px 0' }}>
            <Spin size="large" />
          </div>
        ) : (
          <div>
            <Title level={4}>
              {trackingDetails.consigneeName}
              <Tag color="processing" style={{ marginLeft: 12 }}>
                {trackingDetails.status}
              </Tag>
            </Title>

            <Row gutter={16} style={{ margin: '16px 0' }}>
              <Col span={12}>
                <Text type="secondary">Booked</Text>
                <br />
                <strong>{trackingDetails.booked}</strong>
              </Col>
              <Col span={12}>
                <Text type="secondary">Amount</Text>
                <br />
                <strong>{trackingDetails.amount}</strong>
              </Col>
            </Row>

            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={12}>
                <Text type="secondary">From</Text>
                <br />
                <strong>{trackingDetails.origin}</strong>
              </Col>
              <Col span={12}>
                <Text type="secondary">To</Text>
                <br />
                <strong>{trackingDetails.destination}</strong>
                <br />
                <Text type="secondary">{trackingDetails.consigneeAddress}</Text>
              </Col>
            </Row>

            <Divider />

            <Steps direction="vertical" current={trackingDetails.currentStep} progressDot>
              {trackingDetails.steps.map((step, idx) => (
                <Step key={idx} title={step.title} description={step.description} status={step.status} />
              ))}
            </Steps>

            {trackingDetails.hasPOD && (
              <Card
                title="Proof of Delivery (POD)"
                style={{ marginTop: 24, background: '#f9f9f9' }}
                extra={
                  trackingDetails.podUrl && (
                    <Button
                      type="link"
                      onClick={() => window.open(trackingDetails.podUrl, '_blank')}
                    >
                      Open Full Size
                    </Button>
                  )
                }
              >
                {trackingDetails.podUrl ? (
                  <img
                    src={trackingDetails.podUrl}
                    alt="Proof of Delivery"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '420px',
                      objectFit: 'contain',
                      borderRadius: 8,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    }}
                    onError={(e) => {
                      e.target.src = '';
                      e.target.alt = 'Image failed to load';
                    }}
                  />
                ) : (
                  <Text type="secondary">POD uploaded (preview not available)</Text>
                )}
                {trackingDetails.podFileName && (
                  <div style={{ marginTop: 8 }}>
                    <Text type="secondary">File: {trackingDetails.podFileName}</Text>
                  </div>
                )}
              </Card>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CustomerDashboard;